/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent, ReactNode } from 'react';

import Imgix from 'js/components/Imgix';

import { Typography2, breakpoints, typography2 } from '@coursera/cds-core';

type BaseProps = {
  description: string;
  value: string;
};

type IconProps = {
  isCompact: true;
  icon?: ReactNode;
  iconBackgroundColor?: string;
  ariaLabel?: string;
};

type ImageProps = {
  isCompact?: false;
  imageSrc?: string;
};

type RadioProps = {
  type?: 'radio';
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  radioGroupName?: string;
  isChecked?: boolean;
};

type ButtonProps = {
  type: 'button';
  onClick?: (value: string) => void;
};

type Props = BaseProps & (RadioProps | ButtonProps) & (IconProps | ImageProps);

const styles = {
  goalCard: css`
    /* 
    We would use padding except we are running into problems presumably related to absolute position
    We replace it with margin instead 
    */
    --hacky-margin: var(--cds-spacing-100);

    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 200px;
    width: 100%;

    ${breakpoints.down('sm')} {
      flex-direction: row;
      align-items: center;
      max-width: none;
    }

    border-radius: var(--cds-border-radius-200);
    background: var(--cds-color-neutral-background-primary);
    transition: all 0.3s ease;

    :hover {
      box-shadow: 0 0 40px -8px rgb(0 0 0 / 16%), 0 0 24px -16px rgb(0 0 0 / 16%);
      transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);
    }

    input:checked + label {
      color: var(--cds-color-interactive-primary);
      ${typography2.actionPrimary};
    }
  `,
  goalCardIsCompact: css`
    flex-direction: row;
    max-width: 400px;
    align-items: center;
    width: auto;
  `,
  goalCardPlaceholder: css`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 200px;
    height: 269px;
    padding: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      flex-direction: row;
      align-items: center;
      width: 100%;
      height: 114px;
    }

    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-200);
    background: var(--cds-color-neutral-background-primary);
  `,
  imageContainer: css`
    margin: var(--hacky-margin);
    border-radius: var(--cds-border-radius-100);
    ${breakpoints.down('sm')} {
      width: clamp(100px, 20%, 200px);
    }
  `,
  iconContainer: (iconBackgroundColor?: string) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: var(--hacky-margin);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-100);
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    background-color: ${iconBackgroundColor || 'var(--cds-color-emphasis-primary-background-strong)'};
  `,
  imageContainerPlaceholder: css`
    margin-bottom: var(--cds-spacing-200);
    background: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    height: 164px;
    width: 184px;

    ${breakpoints.down('sm')} {
      height: 97px;
      width: 130px;
      margin: var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-100) 0;
    }
  `,
  interactableElement: (isCompact?: boolean) => css`
    appearance: none;
    position: absolute;

    /* Explicit sizing - more reliable on iOS than right: 0; bottom: 0 */
    width: 100%;
    height: 100%;

    /* We need to use !important here because of a pesky bootstrap style */
    margin: 0 !important;
    top: 0;
    left: 0;

    /* Styling */
    background-color: transparent;
    cursor: pointer;
    border-radius: var(--cds-border-radius-200);
    border-width: ${isCompact ? '1px' : '2px'};
    border-style: solid;
    border-color: var(--cds-color-neutral-stroke-primary-weak);

    :focus {
      outline: 1px solid var(--cds-color-purple-800) !important;
      outline-offset: 2px !important;
    }

    :checked {
      border-color: var(--cds-color-interactive-primary);
    }
  `,
  radioLabel: (isCompact?: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: ${isCompact
      ? 'var(--hacky-margin) var(--cds-spacing-200) var(--hacky-margin) calc(var(--cds-spacing-150) - var(--hacky-margin))'
      : 'calc(var(--cds-spacing-300) - var(--hacky-margin)) var(--cds-spacing-300) var(--cds-spacing-300)'};
    text-align: ${isCompact ? 'left' : 'center'};
    ${isCompact ? typography2.actionSecondary : typography2.bodyPrimary};

    ${breakpoints.down('sm')} {
      margin: ${isCompact
        ? 'var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200) calc(var(--cds-spacing-150) - var(--hacky-margin))'
        : 'var(--cds-spacing-200)'};
      text-align: left;
    }
  `,
  labelPlaceholder: css`
    width: 100%;
    height: 22px;
    background: var(--cds-color-neutral-disabled);
  `,
};

export const GoalCardPlaceholder = () => {
  return (
    <div css={styles.goalCardPlaceholder}>
      <div css={styles.imageContainerPlaceholder} />
      <div css={styles.labelPlaceholder} />
    </div>
  );
};

const GoalCard = ({ description, value, ...inputProps }: Props) => {
  const { isCompact, type } = inputProps;

  const renderFigure = () => {
    if (isCompact === true) {
      const { icon, iconBackgroundColor } = inputProps;

      return <span css={styles.iconContainer(iconBackgroundColor)}>{icon}</span>;
    } else {
      const { imageSrc } = inputProps;

      return (
        <Imgix src={`${imageSrc}?fit=crop&crop=focalpoint&fp-z=1`} alt={description} css={styles.imageContainer} />
      );
    }
  };

  const renderInteractableElement = () => {
    if (type === 'button') {
      const { onClick } = inputProps;

      return (
        <button
          type="button"
          css={styles.interactableElement(isCompact)}
          id={`hiddenRadio-${value}`}
          tabIndex={0}
          value={value}
          onClick={() => onClick?.(value)}
          aria-label={description}
        />
      );
    } else {
      const { isChecked, radioGroupName, onChange } = inputProps;

      return (
        <input
          type="radio"
          css={styles.interactableElement(isCompact)}
          id={`hiddenRadio-${value}`}
          onChange={onChange}
          tabIndex={0}
          name={radioGroupName}
          value={value}
          checked={isChecked}
        />
      );
    }
  };

  return (
    <div css={[styles.goalCard, isCompact && styles.goalCardIsCompact]}>
      {renderFigure()}
      {renderInteractableElement()}
      <label htmlFor={`hiddenRadio-${value}`} css={styles.radioLabel(isCompact)}>
        <Typography2 component="p" color="inherit" variant="inherit">
          {description}
        </Typography2>
      </label>
    </div>
  );
};

export default GoalCard;
